import clsx from "clsx";
import SwitchUI from "../../../components/UI/SwitchUi/SwitchUi";
import { observer } from "mobx-react-lite";
import {useContext, useEffect, useMemo, useState, useRef} from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../..";
import { ReactComponent as AlertIcon } from "../../../assets/icons/alert.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../assets/icons/arrow-leff-circle.svg";
import { ReactComponent as ArrowLeftIconBlack } from "../../../assets/icons/arrow-leff-circle_black.svg";
import { ReactComponent as ArrowLeftIconClose } from "../../../assets/icons/arrow-leff-circle_close.svg";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import ButtonUI from "../../../components/UI/ButtonUI/ButtonUI";
import { priceToUserString, priceToUserStringExact } from "../../../http/basketApi";
import CertificateInput from "../CertificateInput/CertificateInput";
import { pluralize } from "../helpers/pluralize";
import "./totalBasket.scss";

const TotalBasket = observer(({
                                showOrderButton = true ,
                                showPromoAndBonus = true,
                                buttonText = '',
                                disabledButton = false
                              }) => {
  const navigate = useNavigate();
  const {basket, delivery, user} = useContext(Context)
  const [isBonusDescriptionActive, setIsBonusDescriptionActive] =
      useState(false);
  const [promocodeValue, setPromocodeValue] = useState('');
  const [orderSend, setOrderSend] = useState(false);
  const enforced = basket.snapshotMode === 'enforced';
  const receipt = basket.pricingReceipt;
  const pricingReady = !enforced || Boolean(receipt);
  const bonusApplied = enforced ? basket.bonusesConfirmed : basket.bonusesSpend;
  const formatPrice = enforced ? priceToUserStringExact : priceToUserString;
  const mounted = useRef(true);
  useEffect(() => {mounted.current = true; return () => {mounted.current = false;};}, []);

  const toggleBonusDescription = () => {
    if (window.innerWidth <= 1000) {
      setIsBonusDescriptionActive(!isBonusDescriptionActive);
    }
  };

  const discount = useMemo(() => {
    const baseDiscount =
        basket.priceNoSell -
        basket.totalPrice -
        basket.bonusesConfirmed +
        basket.delPrice;
    return baseDiscount > 0 ? baseDiscount : 0;
  }, [
    basket.priceNoSell,
    basket.totalPrice,
    basket.bonusesConfirmed,
    basket.delPrice,
  ]);

  const totalItems = enforced ? receipt?.lines.reduce((count, line) => count + line.count, 0) ?? 0 : basket.basketItems.length;
  const word = pluralize(totalItems, [
    "товар",
    "товара",
    "товаров",
  ]);

  const DELIVERY_VALID = useMemo(() => {
    return !!delivery.officePvz || !!delivery.active
  }, [delivery.officePvz, delivery.active])

  const USER_DATA_VALID = useMemo(() => {
    return (
        delivery.customerInfo.telephone &&
        delivery.customerInfo.name &&
        delivery.customerInfo.surname &&
        delivery.customerInfo.email
    )
  }, [delivery.customerInfo])

  const basketGoOneStep = async () => {
    const context = basket._sessionOperationContext();
    if(!enforced && basket.startBasketNum === 1){
      delivery.clear()
    }
    basket.clearPromoNotificator()

    const STEP_COUNT = basket.startBasketNum === 1 ? 1 : 4
    if(orderSend) return;
    setOrderSend(true)
    let result;
    let navigated = false;
    const onCreated = response => {
      if (mounted.current && basket._sessionOperationCurrent(context)) {
        navigated = true;
        navigate('/order/id/' + response.order.custom_order_id);
      }
    };
    try {result = await basket.upStepPage(STEP_COUNT, onCreated);} finally {if (mounted.current) setOrderSend(false);}
    if (navigated) return;
    if (!mounted.current || enforced && !basket._sessionOperationCurrent(context)) return;

    if (!result) {
      window.scrollTo(0, 0);
      return false;
    }

    if (result.order) {
      navigate("/order/id/" + result.order.custom_order_id);
    }
  };

  useEffect(() => {
    if (enforced && basket.ordinaryCheckoutState === 'accepted' && basket.ordinaryOrderReceipt)
      navigate('/order/id/' + basket.ordinaryOrderReceipt.order.custom_order_id);
  }, [enforced, basket.ordinaryCheckoutState, basket.ordinaryOrderReceipt, navigate]);

  const checkResult = async retry => {
    const context = basket._sessionOperationContext();
    const result = retry ? await basket.retryOrdinaryCheckout() : await basket.recoverOrdinaryCheckout();
    if (mounted.current && basket._sessionOperationCurrent(context) && result?.order) navigate('/order/id/' + result.order.custom_order_id);
  };

  const handleSpendAll = () => {
    if (!basket.bonusesSpendAll) {
      basket.setBonusesSpend(basket.bonusesOrderAvail);
    } else {
      basket.setBonusesSpend(0);
    }
    basket.bonusesSpendAll = !basket.bonusesSpendAll;
  };

  const handlePromocodeChange = (e) => {
    setPromocodeValue(e.target.value);
  }

  const handleDeletePromocode = () => {
    basket.setPromocode(null)
  }

  const handleSubmitPromocode = () => {
    basket.setPromocode(promocodeValue)
  }

  return (
      <div className={"totalBasket"}>
        <div className={"totalBasket__wrapper"}>
          <h5 className={"totalBasket__title"}>Сумма заказа</h5>

          {enforced && !pricingReady && <div role="status">
            {basket.pricingError || (basket.pricingState === 'pending' ? 'Рассчитываем сумму…' : 'Сумма пока не подтверждена')}
            {basket.pricingState === 'error' && <button type="button" onClick={() => basket.retryPricing()}>Повторить расчёт</button>}
          </div>}
          {pricingReady && <div className={"totalBasket__calc"}>
            <div className={"totalBasket__calc-item"}>
            <span>
              {totalItems} {word}:
            </span>
              <span>{formatPrice(basket.priceNoSell)} ₽</span>
            </div>

            {(discount > 0) && (
                <div
                    className={clsx(
                        "totalBasket__calc-item",
                        "totalBasket__calc-item--red"
                    )}
                >
                  <span>Скидка:</span>
                  <span>
                {formatPrice(
                    enforced ? discount : Math.min(discount, basket.totalPrice)
                )}{" "}
                    ₽
              </span>
                </div>
            )}

            {bonusApplied > 0 && (
                <div className={"totalBasket__calc-item"}>
                  <span>Оплата бонусами:</span>
                  <span>{formatPrice(bonusApplied)} ₽</span>
                </div>
            )}

            {!enforced && basket.certificateTotal > 0 && (
                <div className={"totalBasket__calc-item"}>
                  <span>Сертификаты:</span>
                  <span>
                {formatPrice(
                    Math.min(basket.certificateTotal, basket.totalPrice)
                )}{" "}
                    ₽
              </span>
                </div>
            )}

            {basket.delPrice > 0 && (
                <div className={"totalBasket__calc-item"}>
                  <span>Доставка:</span>
                  <span>{enforced ? formatPrice(basket.delPrice) : basket.delPrice} ₽</span>
                </div>
            )}

            <div
                className={clsx(
                    "totalBasket__calc-item",
                    "totalBasket__calc-item--bold"
                )}
            >
              <span>Итого:</span>
              <span>
              {formatPrice(
                  enforced ? receipt.totalPrice : Math.max(basket.totalPrice - basket.certificateTotal, 0)
              )}{" "}
                ₽
            </span>
            </div>
          </div>}
          {enforced && receipt?.deliveryState === 'not_selected' && <div role="status">Доставка не выбрана</div>}
          {enforced && basket.ordinaryCheckoutBusy && <div role="status">{basket.ordinaryCheckoutState === 'pending' ?
            'Сохраняем заказ…' : 'Проверяем результат оформления…'}</div>}
          {enforced && basket.ordinaryCertificateUnsupported && <div role="status">Оплата сертификатом в этом режиме пока недоступна.</div>}
          {enforced && basket.ordinaryCheckoutError && <div role="status">{basket.ordinaryCheckoutError}</div>}
          {enforced && basket._ordinaryIntent && ['unknown', 'not_found'].includes(basket.ordinaryCheckoutState) && <div>
            <ButtonUI text="Проверить результат заказа" onClick={() => checkResult(false)} disabled={basket.ordinaryCheckoutBusy}/>
            {basket.ordinaryCheckoutState === 'not_found' && <ButtonUI text="Повторить оформление с тем же ключом" onClick={() => checkResult(true)}
              disabled={basket.ordinaryCheckoutBusy}/>}
            {basket.ordinaryCheckoutState === 'not_found' && <ButtonUI text="Обновить условия заказа" onClick={() => basket.refreshOrdinaryCheckout()}
              disabled={basket.ordinaryCheckoutBusy}/>}
          </div>}
          {showOrderButton && (
              <ButtonUI onClick={(e) => basketGoOneStep()} text={buttonText} disabled={disabledButton || orderSend ||
                enforced && !basket.ordinaryCheckoutCanAdvance}/>
          )}
        </div>
        {showPromoAndBonus && (
            <>
              <div className="totalBasket__input">
                <input
                    placeholder="Промокод"
                    onChange={handlePromocodeChange}
                />
                {
                  basket.promoDiscountSumm > 0 ?
                      <ArrowLeftIconClose
                          className="totalBasket__input-icon"
                          onClick={handleDeletePromocode}
                      /> : promocodeValue?.length > 0 ? <ArrowLeftIconBlack
                          className="totalBasket__input-icon"
                          onClick={handleSubmitPromocode}
                      /> : <ArrowLeftIcon
                          className="totalBasket__input-icon"
                          onClick={handleSubmitPromocode}
                      />
                }
              </div>

              {basket.promoNitificator && basket.promoNitificator.message && (
                  <div className={`totalBasket__promo-message totalBasket__promo-message--${basket.promoNitificator.color}`}>
                    {basket.promoNitificator.message}
                  </div>
              )}
            </>
        )}
        { showPromoAndBonus && (
            <div className={"totalBasket__bonus"}>
              {
                  user.isAuth && <div className={"totalBasket__bonus-item"}>
              <span className={"totalBasket__bonus-title"}>
                Списать {basket.bonusesOrderAvail} бонусов
                <SwitchUI
                    checked={basket.bonusesSpendAll}
                    onChange={handleSpendAll}
                />
              </span>
                    <span
                        className={`totalBasket__bonus-description ${
                            isBonusDescriptionActive
                                ? "totalBasket__bonus-description--active"
                                : ""
                        }`}
                        onClick={toggleBonusDescription}
                    >
                у вас {basket.bonusesTotal} бонусов
                <AlertIcon />
                <div className={"totalBasket__notifications"}>
                  <span className={"totalBasket__notifications-title"}>
                    Оплата бонусами
                  </span>
                  <span className={"totalBasket__notifications-text"}>
                    Бонусами можно оплатить не&nbsp;более 30% от&nbsp;стоимости
                    товаров в&nbsp;корзине
                  </span>
                  <div
                      onClick={toggleBonusDescription}
                      className={"totalBasket__notifications__close"}
                  >
                    <CloseIcon />
                  </div>
                </div>
              </span>
                  </div>
              }


              <div
                  className={"totalBasket__bonus-item totalBasket__bonus-item--input"}
              >
                {basket.certificates.map((_, i) => (
                    <CertificateInput
                        key={i}
                        index={i}
                        basket={basket}
                    />
                ))}
              </div>
            </div>)
        }
      </div>
  );
});

export default TotalBasket;
